import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { peekSignOutNotice, clearSignOutNotice } from '../../utils/deviceSession';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // Set when the owner signed this device out from /sessions.
  const [info, setInfo] = useState(peekSignOutNotice);
  useEffect(() => { clearSignOutNotice(); }, []);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleReset = async () => {
    setError('');
    setInfo('');
    if (!email) {
      setError('Enter your email address above, then tap “Forgot password?”');
      return;
    }
    try {
      await resetPassword(email.trim());
      setInfo(`Password reset link sent to ${email.trim()}. Check your inbox (and spam).`);
    } catch (err) {
      if (err.code === 'auth/invalid-email') {
        setError('That email address doesn’t look valid.');
      } else {
        setError('Couldn’t send the reset email. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const code = err.code;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else if (code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (code === 'auth/user-disabled') {
        setError('This account has been blocked. Contact the owner.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Login failed. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      {/* Left — brand panel (desktop only) */}
      <div className="login-panel">
        <div className="login-panel__inner">
          <span className="login-panel__tag">🍽️ Authentic Indian Cuisine</span>
          <h2 className="login-panel__title">Welcome back to<br /><span>Desi Bites</span></h2>
          <p className="login-panel__sub">Sign in to manage your account, save your favorites, and explore our menu.</p>
          <div className="login-panel__features">
            <div className="login-panel__feature"><span>🥘</span> Fresh handcrafted dishes daily</div>
            <div className="login-panel__feature"><span>⚡</span> Fast & easy ordering</div>
            <div className="login-panel__feature"><span>📍</span> 226 N Main St, Centerton AR</div>
          </div>
        </div>
        <div className="login-panel__overlay" />
      </div>

      {/* Right — form */}
      <div className="login-form-wrap">
        <div className="login-form-card">
          {/* Logo */}
          <div className="login-brand">
            <div className="login-brand__icon">🍛</div>
            <div className="login-brand__name">Desi Bites</div>
          </div>

          <h1 className="login-title">Sign In</h1>
          <p className="login-sub">Enter your credentials to continue</p>

          {error && (
            <div className="login-error">
              <span>⚠️</span> {error}
            </div>
          )}
          {info && (
            <div className="login-error" role="status" style={{ background: 'rgba(34,197,94,0.12)', color: '#15803d', borderColor: 'rgba(34,197,94,0.3)' }}>
              <span>✅</span> {info}
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label">Email Address</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </span>
                <input
                  className="login-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label">Password</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                </span>
                <input
                  className="login-input"
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-input-toggle"
                  onClick={() => setShowPass(s => !s)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                >
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <button
                  type="button"
                  className="login-link"
                  onClick={handleReset}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? (
                <span className="login-btn__spinner" />
              ) : (
                <>Sign In <span>→</span></>
              )}
            </button>
          </form>

          <div className="login-divider"><span>OR</span></div>

          <div className="login-footer">
            Don't have an account?{' '}
            <Link to="/signup" className="login-link">Create one →</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
